import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Api } from './api';
import { DomainService } from './domain';

@Injectable({ providedIn: 'root' })
export class AdmissionStateService {
  private api = inject(Api);
  private domainService = inject(DomainService);
  private admissionSubject = new BehaviorSubject<any | null>(null);

  readonly admission$ = this.admissionSubject.asObservable();

  get currentAdmission(): any | null {
    return this.admissionSubject.value;
  }

  setAdmission(data: any | null): void {
    this.admissionSubject.next(data ?? null);
  }

  clearAdmission(): void {
    this.admissionSubject.next(null);
  }

  fetchAdmissionById(id: number): Observable<any | null> {
    if (!id) {
      this.clearAdmission();
      return of(null);
    }

    return this.domainService.ensureDomainContext().pipe(
      switchMap((isReady) => {
        if (!isReady) {
          this.clearAdmission();
          return of(null);
        }

        return this.api.post<any>('v2.0', 'getStudentAdmissionById', { Id: id }).pipe(
          map((res) => (!res?.err && res?.data ? res.data : null)),
          tap((data) => {
            if (data) {
              this.setAdmission(data);
            }
          }),
          catchError(() => of(null)),
        );
      }),
      catchError(() => of(null)),
    );
  }

  ensureAdmissionById(id: number): Observable<any | null> {
    const current = this.currentAdmission;
    if (current?.Id === id) {
      return of(current);
    }

    return this.fetchAdmissionById(id);
  }

  updateAdmission(partial: any): void {
    if (!partial) return;
    const current = this.currentAdmission || {};
    this.setAdmission(this.deepMerge(current, partial));
  }

  updateDeclarations(declarations: any): void {
    if (!declarations) return;
    this.updateAdmission({
      Declarations: {
        ...(this.currentAdmission?.Declarations || {}),
        ...declarations,
      },
    });
  }

  updateDocuments(documents: any[]): void {
    this.updateAdmission({ Documents: documents || [] });
  }

  updateTransportMode(mode: string | null): void {
    this.updateAdmission({ TransportMode: mode });
  }

  private deepMerge(target: any, source: any): any {
    if (!this.isPlainObject(target) || !this.isPlainObject(source)) {
      return source;
    }

    const merged: any = { ...target };
    Object.keys(source).forEach((key) => {
      const sourceValue = source[key];
      const targetValue = merged[key];

      if (Array.isArray(sourceValue)) {
        merged[key] = [...sourceValue];
        return;
      }

      if (this.isPlainObject(sourceValue) && this.isPlainObject(targetValue)) {
        merged[key] = this.deepMerge(targetValue, sourceValue);
        return;
      }

      merged[key] = sourceValue;
    });

    return merged;
  }

  private isPlainObject(value: any): boolean {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }
}
